import type { PaigeClient } from '@paige/api-client';

/** openapi-fetch's middleware shape, taken from the client so the app needs no dependency of its own. */
type Middleware = Parameters<PaigeClient['use']>[0];

/**
 * "Hide test companies": one switch for the whole admin area, remembered per staff member in this browser.
 * On (the default), every admin read that lists or totals companies asks the API to leave Ademero's own
 * test companies out (`tests=hide`), so the lists and the totals on every page are over the same companies.
 * Prod's audit of 2026-10-07 found 23 of 28 companies were ours and no page could tell.
 */
const KEY = 'paige.admin.hideTestCompanies';

/** The admin reads that take `tests` (paige-v1.yaml, AdminTests). */
const PATHS = [
  '/admin/companies',
  '/admin/customers',
  '/admin/customers/summary',
  '/admin/today',
  '/admin/funnel',
  '/admin/campaigns',
  '/admin/usage',
  '/admin/friction',
  '/admin/revenue',
  '/admin/retention',
  '/admin/problems',
  '/admin/timeline/recent',
  '/admin/flow',
  '/admin/flow/items',
  '/admin/flow/incidents',
  '/admin/flow/incidents/companies',
  '/admin/flow/history',
];

let person = 'staff';
const listeners = new Set<() => void>();

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // storage refused (a locked-down browser): the default stands for the session
  }
}

const keyOf = (who: string): string => `${KEY}.${who}`;

/** Whose choice is read from here on: the signed-in staff member's id. */
export function hideTestsFor(who: string): void {
  if (who === person) return;
  person = who;
  for (const listener of listeners) listener();
}

/** True unless this staff member switched it off. */
export function hideTests(): boolean {
  return storage()?.getItem(keyOf(person)) !== 'show';
}

export function setHideTests(hide: boolean): void {
  storage()?.setItem(keyOf(person), hide ? 'hide' : 'show');
  for (const listener of listeners) listener();
}

export function subscribeHideTests(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The middleware to `client.use`: adds `tests=hide` to the admin reads that take it while the switch is on. */
export function hideTestsMiddleware(): Middleware {
  return {
    onRequest({ request, schemaPath }) {
      if (request.method.toUpperCase() !== 'GET' || !PATHS.includes(schemaPath) || !hideTests())
        return request;
      const url = new URL(request.url);
      if (url.searchParams.has('tests')) return request;
      url.searchParams.set('tests', 'hide');
      return new Request(url, request);
    },
  };
}
